import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import PageHeader from "../PageHeader";
import UserAvatar from "../people/UserAvatar";
import useHouseholdUsers from "../../hooks/useHouseholdUsers";
import { activityApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { describeEvent } from "./activityText";
import { dayLabel, headlineParts, tally } from "./activityFeedText";
import { displayName, relativeTime, userLabel } from "./userDisplay";

// The reader's zone decides where a day starts (activity-feed D3).
const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const MAX_AVATARS = 3;

function ActivityRow({ row }) {
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  // Ranked by the server across the whole day (D4), not just these five.
  const headline = row.headline ?? row.events[0];
  const { actor, verb, suffix } = headlineParts(headline);
  const others = row.actors
    .filter((a) => a.id !== headline?.actor?.id)
    .map(displayName);
  const more = tally(row.counts, headline);
  const hidden = row.event_count - row.events.length;

  return (
    <li className="rounded-lg border bg-card p-4 text-card-foreground">
      <div className="flex items-start gap-3">
        <span className="flex shrink-0 -space-x-2 pt-0.5" aria-hidden="true">
          {row.actors.slice(0, MAX_AVATARS).map((a) => (
            <UserAvatar
              key={a.id}
              user={a}
              size="sm"
              decorative
              className="ring-2 ring-card"
            />
          ))}
        </span>
        <div className="min-w-0 flex-1">
          <p className="m-0">
            <strong>{actor}</strong> {verb}{" "}
            <Link
              to={`/tasks/board?task=${row.task.id}`}
              className="font-medium underline-offset-4 hover:underline"
            >
              {row.task.title}
            </Link>
            {suffix}
            {row.task.archived && (
              <span className="text-muted-foreground"> (archived)</span>
            )}
          </p>
          <p className="m-0 text-sm text-muted-foreground">
            {[
              more,
              others.length > 0 && `with ${others.join(", ")}`,
              row.task.project_name,
            ]
              .filter(Boolean)
              .join(" · ")}
            {(more || others.length > 0 || row.task.project_name) && " · "}
            <time dateTime={row.latest_at} title={row.latest_at}>
              {relativeTime(row.latest_at)}
            </time>
          </p>
          {open && (
            <div id={detailsId} className="mt-2 border-l-2 pl-3">
              <ul className="m-0 list-none space-y-1.5 p-0 text-sm">
                {row.events.map((e) => (
                  <li key={e.id}>
                    <span className="text-foreground">
                      {e.actor ? displayName(e.actor) : "Someone"}
                    </span>{" "}
                    <span className="text-muted-foreground">
                      {describeEvent(e) || "commented"}
                    </span>{" "}
                    <span className="text-xs text-muted-foreground">
                      {relativeTime(e.created_at)}
                    </span>
                    {e.verb === "comment" && e.payload?.body && (
                      <div className="mt-0.5 line-clamp-3 whitespace-pre-wrap">
                        {e.payload.body}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {hidden > 0 && (
                <p className="m-0 mt-1.5 text-sm text-muted-foreground">
                  …and {hidden} earlier that day —{" "}
                  <Link to={`/tasks/board?task=${row.task.id}`}>
                    open the task
                  </Link>{" "}
                  for the whole thread.
                </p>
              )}
            </div>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="shrink-0"
          aria-expanded={open}
          aria-controls={open ? detailsId : undefined}
          aria-label={`${open ? "Hide" : "Show"} details for ${row.task.title}`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Hide" : "Details"}
        </Button>
      </div>
    </li>
  );
}

const userShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  username: PropTypes.string,
});

ActivityRow.propTypes = {
  row: PropTypes.shape({
    key: PropTypes.string.isRequired,
    day: PropTypes.string.isRequired,
    latest_at: PropTypes.string.isRequired,
    event_count: PropTypes.number.isRequired,
    task: PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      archived: PropTypes.bool,
      project_name: PropTypes.string,
    }).isRequired,
    actors: PropTypes.arrayOf(userShape).isRequired,
    counts: PropTypes.objectOf(PropTypes.number).isRequired,
    headline: PropTypes.object,
    events: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
};

/**
 * What happened on tasks you can see while you were away
 * (projects/activity-feed). One row per task per day, newest first; your
 * own actions are left out unless you ask; a line marks what's new since
 * you last opened it.
 */
function ActivityFeed() {
  const { users } = useHouseholdUsers();
  const [actor, setActor] = useState("");
  const [kind, setKind] = useState("");
  const [mine, setMine] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [failedPage, setFailedPage] = useState(1);
  // undefined: not known yet; null: never looked before; else an ISO time.
  const [seenAt, setSeenAt] = useState(undefined);
  const [reloadTick, setReloadTick] = useState(0);

  // Only the newest request may land (a filter change while a page loads).
  const request = useRef(0);
  const loadPage = useCallback(
    async (n) => {
      const id = ++request.current;
      setLoading(true);
      setError(null);
      if (n === 1) {
        // A new filter (or Refresh) starts over: the old filter's rows
        // must not sit under the new controls, nor "Show older" extend them.
        setRows([]);
        setHasMore(false);
      }
      const params = { tz: TIME_ZONE, page: n };
      if (actor) params.actor = actor;
      if (kind) params.kind = kind;
      if (mine) params.mine = true;
      try {
        const { data } = await activityApi.list(params);
        if (id !== request.current) return;
        const results = data.results || [];
        // Page 1 started from no rows; a later page can bring round a row
        // that gained an event since — keep the first.
        setRows((prev) => {
          const have = new Set(prev.map((r) => r.key));
          return [...prev, ...results.filter((r) => !have.has(r.key))];
        });
        setPage(n);
        setHasMore(Boolean(data.next));
      } catch (err) {
        if (id !== request.current) return;
        if (n > 1 && err?.response?.status === 404) {
          // The rows shifted and that page no longer exists: nothing older.
          setHasMore(false);
          return;
        }
        setError(apiErrorMessage(err, "Couldn't load the activity."));
        setFailedPage(n);
      } finally {
        if (id === request.current) setLoading(false);
      }
    },
    [actor, kind, mine],
  );

  useEffect(() => {
    loadPage(1);
  }, [loadPage, reloadTick]);

  // Mark the feed seen once per visit, keeping the time it replaced for
  // the "new since" line. A ref, not a cancel flag: StrictMode's second
  // effect run must neither POST again (it would return THIS visit's time)
  // nor drop the first answer.
  const markedSeen = useRef(false);
  useEffect(() => {
    if (markedSeen.current) return;
    markedSeen.current = true;
    activityApi
      .seen()
      .then(({ data }) => setSeenAt(data.seen_at ?? null))
      .catch(() => setSeenAt(null));
  }, []);

  const filtered = Boolean(actor || kind);
  const isNew = (row) =>
    seenAt ? Date.parse(row.latest_at) > Date.parse(seenAt) : false;
  const nothingNew = seenAt && rows.length > 0 && !isNew(rows[0]);
  // The divider sits above the first row you'd already seen, when there
  // are new rows above it.
  const dividerKey =
    seenAt && rows.length > 0 && isNew(rows[0])
      ? rows.find((r) => !isNew(r))?.key
      : undefined;

  // By day, in first-seen order: an appended page can carry a day that's
  // already on screen, and it joins that section (one key per day).
  const byDay = new Map();
  for (const row of rows) {
    if (!byDay.has(row.day)) byDay.set(row.day, []);
    byDay.get(row.day).push(row);
  }
  const days = [...byDay].map(([day, dayRows]) => ({ day, rows: dayRows }));

  return (
    <div className="page-container mt-6 pt-4" style={{ maxWidth: 880 }}>
      <PageHeader
        title="Activity"
        subtitle="What happened on tasks you can see while you were away — one line per task per day, newest first."
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="activity-who">Who</Label>
          <NativeSelect
            id="activity-who"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
          >
            <option value="">Anyone</option>
            {users.map((u) => (
              <option key={u.id} value={String(u.id)}>
                {userLabel(u)}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="activity-what">What</Label>
          <NativeSelect
            id="activity-what"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            <option value="">Everything</option>
            <option value="comments">Comments</option>
            <option value="finished">Finished tasks</option>
          </NativeSelect>
        </div>
        {/* Picking someone shows them, you included, so the toggle only
            applies to "Anyone". */}
        {!actor && (
          <div className="flex min-h-9 items-center gap-2">
            <Checkbox
              id="activity-mine"
              checked={mine}
              onCheckedChange={(v) => setMine(v === true)}
            />
            <Label htmlFor="activity-mine" className="font-normal">
              Include my own
            </Label>
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => setReloadTick((t) => t + 1)}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">{error}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => loadPage(failedPage)}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {nothingNew && (
        <p className="mb-3 text-sm text-muted-foreground" role="status">
          Nothing new since you last looked — here&apos;s what came before.
        </p>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-lg border bg-card p-4 text-muted-foreground">
          {filtered
            ? "Nothing matches these filters."
            : mine
              ? "Nothing yet — when people and agents work on tasks you can see, it shows up here."
              : "Nothing from anyone else yet — when people and agents work on tasks you can see, it shows up here. Your own changes are hidden; tick “Include my own” to see them."}
        </div>
      )}

      {days.map(({ day, rows: dayRows }) => {
        const headingId = `activity-day-${day}`;
        // The line above the first row you'd seen: between sections when
        // that row starts its day, else inside the day's list.
        const lineFirst = dayRows[0].key === dividerKey;
        return (
          <div key={day}>
            {lineFirst && <NewSinceLine as="div" />}
            <section aria-labelledby={headingId} className="mb-5">
              <h2
                id={headingId}
                className="mb-2 text-sm font-medium tracking-wide text-muted-foreground uppercase"
              >
                {dayLabel(day)}
              </h2>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {dayRows.map((row, i) => (
                  <FeedItem
                    key={row.key}
                    row={row}
                    divider={i > 0 && row.key === dividerKey}
                  />
                ))}
              </ul>
            </section>
          </div>
        );
      })}

      {loading && (
        <div className="py-6 text-center" role="status">
          <Loader2 className="inline size-5 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading activity…</span>
        </div>
      )}

      {!loading && !error && hasMore && (
        <div className="mb-8 text-center">
          <Button variant="outline" onClick={() => loadPage(page + 1)}>
            Show older
          </Button>
        </div>
      )}
    </div>
  );
}

const NEW_SINCE = "Above: new since you last looked";

/** A labelled separator, as an <li> inside a day's list or a <div> between. */
function NewSinceLine({ as: Tag = "li" }) {
  return (
    <Tag
      role="separator"
      aria-label={NEW_SINCE}
      className="mb-2 flex list-none items-center gap-2 text-xs font-medium tracking-wide text-primary uppercase"
    >
      <span className="h-px flex-1 bg-primary/40" aria-hidden="true" />
      <span aria-hidden="true">{NEW_SINCE}</span>
      <span className="h-px flex-1 bg-primary/40" aria-hidden="true" />
    </Tag>
  );
}

NewSinceLine.propTypes = {
  as: PropTypes.oneOf(["li", "div"]),
};

function FeedItem({ row, divider }) {
  return (
    <>
      {divider && <NewSinceLine />}
      <ActivityRow row={row} />
    </>
  );
}

FeedItem.propTypes = {
  row: ActivityRow.propTypes.row,
  divider: PropTypes.bool,
};

export default ActivityFeed;
